import { useMemo, useState } from "react";
import clsx from "clsx";
import { ASK_AI_MESSAGE_TYPES } from "../../constants/AskAIConstants";
import { formatAIResponse } from "../../utils/formatAIResponse";
import { copyMessageContent, htmlToPlainText } from "../../utils/copyToClipboard";
import SmartActionButtons from "./SmartActionButtons";

const AskAIMessageBox = ({ message, onActionClick }) => {
    const isUserMessage = message.type === ASK_AI_MESSAGE_TYPES.USER;
    const isSmartActions = message.type === ASK_AI_MESSAGE_TYPES.SMART_ACTIONS;
    const [isCopied, setIsCopied] = useState(false);

    // Format AI responses with rich text formatting
    const formattedText = useMemo(() => {
        if (!message.text) return null;

        // Only format AI responses, keep user messages as plain text
        if (isUserMessage) {
            return message.text;
        }

        // Format AI response with markdown-like syntax
        return formatAIResponse(message.text);
    }, [message.text, isUserMessage]);

    // Handle copy button click
    const handleCopy = async () => {
        try {
            let success = false;

            // Prepare copy data based on message content
            const copyData = {
                imageDataUrl: message.image || null,
                htmlContent: isUserMessage ? null : formattedText,
                plainText: message.text || null,
            };

            // If AI message with formatted content, provide plain text fallback
            if (!isUserMessage && formattedText) {
                copyData.plainText = htmlToPlainText(formattedText);
            }

            // Copy message content (handles image, text, or both)
            success = await copyMessageContent(copyData);

            if (success) {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000); // Reset after 2 seconds
            }
        } catch (error) {
            console.error("Failed to copy message:", error);
        }
    };

    // If this is a Smart Actions message, render SmartActionButtons component
    // No bubble container - buttons appear as clean extension below AI response
    if (isSmartActions) {
        // Don't render if message is hidden
        if (message.hidden) {
            return null;
        }

        return (
            <div className='askai-smart-actions-message askai-smart-actions-inline'>
                <SmartActionButtons
                    actions={message.actions || []}
                    onActionClick={onActionClick}
                    isLoading={false}
                    disabled={false}
                    showHeader={false}
                />
            </div>
        );
    }

    return (
        <div className={clsx("askai-message", isUserMessage ? "user-message" : "ai-message")}>
            <div className='askai-message-content'>
                {message.image && (
                    <div className='askai-message-image'>
                        <img src={message.image} alt='User screenshot' />
                    </div>
                )}
                {formattedText && (
                    <div className='askai-message-text'>
                        {!isUserMessage && (
                            <div className='askai-message-header'>
                                <h6 className='askai-message-title'>AI Response</h6>
                            </div>
                        )}
                        {isUserMessage ? (
                            <p>{formattedText}</p>
                        ) : (
                            <div className='formatted-content' dangerouslySetInnerHTML={{ __html: formattedText }} />
                        )}
                    </div>
                )}

                {/* Copy Button - Only for AI messages, positioned inside bubble */}
                {!isUserMessage && (
                    <div className='askai-message-copy'>
                        <div className='askai-message-copy-button custom-tooltip center-tooltip tooltip-auto-width'>
                            <button className={clsx("askai-copy-btn", isCopied && "copied")} onClick={handleCopy} aria-label='Copy message'>
                                {isCopied ? <span className='for-icon icon-check-light'></span> : <span className='for-icon icon-copy-code'></span>}
                            </button>
                            <div className='custom-tooltip-content text-nowrap'>
                                <div className='custom-tooltip-in'>
                                    <p>{isCopied ? "Copied!" : "Copy"}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AskAIMessageBox;
